// #region Imports
import { styled } from "@mui/material";
import type { ForumRequest } from "messaging/conversations/conversation-links";
import CircularProgress from "@mui/material/CircularProgress";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useImagePreviewDialog } from "_shared/_providers/dialogs/image-preview/image-preview-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import Individual from "marketplace/individuals/individual";
import { messagingApi } from "messaging/_api/messaging-api";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import MessageQuery from "messaging/_api/queries/message-query";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import {
  getIsLoadingMessagesByForumId,
  getMessagesByForumId,
} from "messaging/_stores/messages-redux-slice";
import { loadThroughOldestUnread } from "messaging/_stores/load-through-oldest-unread";
import { getForumUnreadCounts } from "messaging/forums/_stores/forum-unread-counts-redux-slice";
import MessageEditor, { type SentAttachmentOption } from "messaging/_views/components/editor/message-editor";
import { ReceivedMessage } from "messaging/_views/components/received-message-container";
import { SentMessage } from "messaging/_views/components/sent-message-container";
import AttachmentParameters, {
  AttachmentFilterParameters,
} from "messaging/attachments/_api/queries/attachment-parameters";
import AttachmentQuery from "messaging/attachments/_api/queries/attachment-query";
import {
  getIsLoadingSharedFiles,
  getSharedFiles,
  populateSharedFiles,
} from "messaging/attachments/_stores/message-attachments-redux-slice";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import SharedFilesList from "messaging/attachments/_views/shared-files-list";
import {
  CorrespondenceBody,
  CorrespondenceComposerSlot,
  CorrespondenceEmptyText,
  CorrespondenceFrame,
  CorrespondenceJumpToBottom,
  CorrespondenceHeader,
  CorrespondenceSearchBar,
  correspondenceListPadding,
} from "messaging/correspondence/_views/correspondence-layout";
import CorrespondenceTabs, { SHARED_FILES_TAB } from "messaging/correspondence/_views/correspondence-tabs";
import ConversationDayDivider, {
  withConversationDays,
} from "messaging/correspondence/_views/conversation-day-divider";
import { getForumChatTitle, getForumReadOnlyNotice } from "messaging/forums/forum-chat-display";
import ForumChatLoadingSkeleton from "messaging/forums/_views/components/forum-chat-loading-skeleton";
import TimelineOverlay from "_shared/_views/timelines/timeline-overlay";
import useForumTimelineRange from "messaging/forums/_views/components/_hooks/use-forum-timeline-range";
import useForumReadState from "messaging/forums/_views/components/_hooks/use-forum-read-state";
import { usePaneFollowsNewestMessage } from "messaging/forums/_views/components/_hooks/use-pane-follows-newest-message";
import useScrollbarPress from "_shared/_views/timelines/use-scrollbar-press";
import useTimelineIdleFade from "_shared/_views/timelines/use-timeline-idle-fade";
import Forum from "messaging/forums/forum";
import ForumParticipant from "messaging/forums/subscribers/forum-participant";
import SubscribersComponent from "messaging/forums/subscribers/_views/subscriber-list";
import AddSubscriber from "messaging/forums/subscribers/invitations/_views/invite-forum-subscriber-view";
import Message from "messaging/message";
import AHDate from "_shared/dates/date";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { ATTACHMENT_DOWNLOAD_FAILURE } from "messaging/attachments/attachment-download-failure";
import { Fragment, useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
// #endregion

// #region Styled Components
// How near the top of the pane a reader must be for the older page to load: useInfiniteScroll's own default.
const olderPageScrollThreshold = 20;

const MessageLoadingIndicator = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: "32px",
  margin: theme.spacing(2),
  width: "32px",
}));
/** Said where the message editor would be, when the reader may not write in the forum. */
const ReadOnlyNotice = styled("p")(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1.5, 2),
  color: theme.palette.text.secondary,
  ...theme.typography.body2,
}));

// Sticks to the far end of the pane from the newest entry: the head of it for messages (bottom-up,
// the oldest page loads above what is already there), the foot of it for shared files (kept newest
// first regardless of conversation order, so their older page always loads below).
const OlderPageLoadingIndicatorContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "atFoot",
})<{ atFoot: boolean }>(({ atFoot, theme }) => ({
  backgroundColor: theme.palette.background.paper,
  bottom: atFoot ? 0 : undefined,
  display: "flex",
  justifyContent: "center",
  paddingBottom: "8px",
  position: "sticky",
  top: atFoot ? undefined : 0,
  width: "100%",
}));
// The scrubber is positioned against this, not against the box: the box holds the search header too,
// and inside the list the scrubber would scroll away with the messages.
const MessageListFrame = styled("div")(() => ({
  display: "grid",
  minHeight: 0,
  position: "relative",
}));
/**
 * The list inside the scrolling pane. It takes the pane's full height while it holds only a notice
 * ("No Messages", "No Shared Files", a lock), so the notice's own `height: 100%` resolves and centers it
 * in the box rather than at the top; otherwise it is as tall as its messages.
 */
const MessageList = styled("div", {
  shouldForwardProp: (prop) => prop !== "showsNotice",
})<{ showsNotice: boolean }>(({ showsNotice }) => ({
  height: showsNotice ? "100%" : undefined,
}));
const MessagePane = styled("div", {
  shouldForwardProp: (prop) => prop !== "hideScrollbar",
})<{ hideScrollbar: boolean }>(({ hideScrollbar, theme }) => ({
  ...correspondenceListPadding(theme),
  containerType: "inline-size",
  minHeight: 0,
  overflowY: hideScrollbar ? "hidden" : "auto",
  position: "relative",
  scrollbarGutter: "stable",
  width: "100%",
}));
/** The box the Documents panel fills, in the place and with the edges of the conversation's own box. */
const DocumentsSlot = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: "1px solid #aaa",
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  borderTop: "none",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gridRowStart: 2,
  minHeight: 0,
  overflow: "hidden",
  width: "100%",
}));
// #endregion

/** The files tab, when it holds the work's documents rather than the chats' attachments. */
const DOCUMENTS_TAB = { label: "Documents", icon: <DescriptionOutlinedIcon fontSize="small" /> };

export type UserAvatarMap = {
  [userId: string]: string;
};

type ChatWindowProps = {
  className?: string;
  fillAvailableSpace?: boolean;
  forums: Forum[];
  locked?: boolean;
  lockedMessage?: string;
  readOnly?: boolean;
  /**
   * The window is on screen. Opening a conversation marks what it holds read, so a window kept mounted
   * behind a closed drawer says so and marks nothing. Defaults to true.
   */
  isOpen?: boolean;
  /**
   * Leaves out the member list. A work agreement's forums take their members from its teams, and the
   * pane says who reads each one in its tab, so the list is only noise there (#5823).
   */
  hideMembers?: boolean;
  /**
   * The conversation a link asked the window to open on (#5826). One the reader cannot see leaves the
   * window on its default tab.
   */
  forumRequest?: ForumRequest;
  /**
   * Stands in for the Shared files tab: the tab is titled Documents, and opening it shows this in
   * place of the conversation. A work agreement's pane passes the work's own documents, which are
   * not chat attachments (#6054). Without it, the tab lists the open forums' attachments.
   */
  documents?: ReactNode;
  /**
   * What a conversation's tab says, when the host knows better than the forum's own name. Returning
   * undefined keeps the default title.
   */
  describeConversation?: (forum: Forum) => ConversationDescription | undefined;
  /**
   * Offers Copy to documents on every file in the conversation, and is called with the file and the
   * conversation it was sent in. A work agreement's pane copies it into the work's documents (#6053).
   */
  onCopyAttachment?: (attachment: MessageFileInfo, forum: Forum) => void;
  /**
   * Offered beside a file attached in the editor, and acted on once the message is sent. A work
   * agreement's pane offers to add the file to the work's documents too (#6098).
   */
  attachmentOption?: SentAttachmentOption;
  onClose?: () => void;
  onLeaveConversation?: () => void;
};

/**
 * A conversation tab's title, and who reads what the reader sends there. The tab carries no other
 * line since #6067; the audience is said in a notice pinned above the messages instead (#6875).
 */
export type ConversationDescription = {
  title: string;
  /** What the conversation's tab shows, when the title says more than a tab needs. */
  shortTitle?: string;
  audienceNotice?: string;
};

/** A link's request for its forum's oldest unread message, until the window answers it (#6585). */
type UnreadArrival = {
  key: string;
  forumId: string;
  /** The forum's messages have started loading since the link: what loads next is current. */
  loadStarted: boolean;
};

/**
 * The visible forum `forum` names, ignoring case: by id, by name, or by the title its tab shows when
 * the host retitles it ("Work Correspondence" for the work's "All Stakeholders", #6054).
 */
export function findRequestedForum(
  forums: readonly Forum[],
  forum: string | undefined,
  describeConversation?: (forum: Forum) => ConversationDescription | undefined,
): Forum | undefined {
  const wanted = forum?.trim().toLocaleLowerCase();
  if (!wanted) return undefined;
  return (
    forums.find((candidate) => candidate.id?.value.toLocaleLowerCase() === wanted) ??
    forums.find((candidate) => candidate.name?.value?.trim().toLocaleLowerCase() === wanted) ??
    forums.find((candidate) => describeConversation?.(candidate)?.title.toLocaleLowerCase() === wanted)
  );
}

/**
 * Deletes the author's message. Outside the component for the same reason as `saveMessageEdit`.
 */
async function deleteOwnMessage(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  message: Message,
): Promise<boolean> {
  try {
    await dispatch(messagingApi.endpoints.deleteMessage.initiate({ messageId: message.id })).unwrap();
    return true;
  } catch (error) {
    console.error("Failed to delete the message", error);
    enqueueSnackbar("We couldn't delete your message.", { variant: "error" });
    return false;
  }
}

/**
 * Saves the author's rewrite of their message. Outside the component because it needs a `catch`, and
 * the React Compiler skips a component whose code it cannot lower.
 */
async function saveMessageEdit(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  message: Message,
  text: string,
): Promise<boolean> {
  try {
    await dispatch(messagingApi.endpoints.updateMessage.initiate({ messageId: message.id, content: text })).unwrap();
    return true;
  } catch (error) {
    console.error("Failed to save the message edit", error);
    enqueueSnackbar("We couldn't save your message.", { variant: "error" });
    return false;
  }
}

type ActiveForumSubscriberData = {
  forumId?: string;
  /** Everyone the forum names, those who wrote in it and have since left included (#6878). */
  participants: ForumParticipant[];
  avatars: UserAvatarMap;
};

/**
 * These two live outside the component on purpose. Both need a `finally` — one to unsubscribe
 * the request, one to clear the loading flag — and the React Compiler cannot lower a `finally`,
 * so leaving them inline made it skip memoizing the whole chat window. Module-level functions
 * are not compiled, so the original control flow is preserved exactly.
 */
async function loadForumSubscribers(
  forum: Forum,
  abortController: AbortController,
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  fetchUserAvatars: (subscribers: Individual[]) => Promise<UserAvatarMap>,
  setActiveForumSubscriberData: (data: ActiveForumSubscriberData) => void,
): Promise<void> {
  if (!forum.id) return;
  // A superseded load is discarded by the `aborted` checks below rather than cancelled, because
  // `initiate` hands back a handle on a cache entry shared by everyone asking for this forum.
  // Aborting it writes the abort error into that shared entry, and the load superseding this one
  // re-initiates the same entry in the same tick — RTK Query sees the first request still pending,
  // skips the second thunk, and settles it from the entry the abort has just failed. The window
  // then reports "Failed to get subscribers" for a request nothing cancelled, and no later load
  // re-requests it. Letting the request finish instead fills the entry, so the load that
  // superseded this one reads the subscribers straight out of it.
  const request = dispatch(
    messagingApi.endpoints.getForumSubscriberInfo.initiate({
      forumId: forum.id.value,
    })
  );

  try {
    const participants = await request.unwrap();
    const userAvatars = await fetchUserAvatars(participants.map((participant) => participant.individual));
    if (abortController.signal.aborted) return;

    setActiveForumSubscriberData({
      forumId: forum.id?.value,
      participants,
      avatars: userAvatars,
    });
  } catch (err) {
    if (abortController.signal.aborted) return;
    console.error(err);
    enqueueSnackbar("Failed to get subscribers", { variant: "error" });
  } finally {
    request.unsubscribe();
  }
}

async function loadForumSubscribersAndAvatars(
  forum: Forum,
  abortController: AbortController,
  loadSubscribers: (forum: Forum, abortController: AbortController) => Promise<void>,
  setIsLoadingForum: (isLoading: boolean) => void,
): Promise<void> {
  setIsLoadingForum(true);
  try {
    await loadSubscribers(forum, abortController);  } finally {
    if (!abortController.signal.aborted) {
      setIsLoadingForum(false);
    }
  }
}

/**
 * Loads older pages until the oldest unread message is in the store, and answers with its id. Outside
 * the component because it needs a `catch`.
 */
async function loadOldestUnreadMessageId(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  forumId: string,
  readerId: Guid,
): Promise<string | undefined> {
  try {
    return await dispatch(loadThroughOldestUnread({ forumId, readerId })).unwrap();
  } catch (error) {
    console.error("Failed to load the oldest unread message", error);
    enqueueSnackbar("We couldn't load the oldest unread message.", { variant: "error" });
    return undefined;
  }
}

// A scan, not the last entry: the list is kept oldest first, but its newest entry may have no created date.
function newestCreatedDate(messages: readonly Message[] | undefined): AHDate | undefined {
  let newest: AHDate | undefined;
  for (const message of messages ?? []) {
    if (message.created && (!newest || message.created.isAfter(newest))) newest = message.created;
  }
  return newest;
}

/**
 * The moment a message is shown at, for the day divider above it: the moment its bubble shows, or its
 * creation for one not yet published. One just sent may have neither until the server answers, and it
 * is today's.
 */
export function messageMomentOf(message: Message): AHDate {
  return message.published ?? message.created ?? new AHDate();
}

export default function ChatWindow(props: Readonly<ChatWindowProps>) {
  const {
    className,
    forums,
    forumRequest,
    locked,
    lockedMessage,
    readOnly,
    hideMembers,
    documents,
    describeConversation,
    onCopyAttachment,
    attachmentOption,
    onClose,
    onLeaveConversation,
  } = props;
  const fillAvailableSpace = props.fillAvailableSpace ?? false;
  const isOpen = props.isOpen ?? true;

  // #region Refs
  /** The foot of the message list: the newest message, and where the editor sits just below. */
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagePaneRef = useRef<HTMLDivElement>(null);
  /** Everything the pane scrolls, measured so the pane can stay at its foot while it settles. */
  const messageListRef = useRef<HTMLDivElement>(null);
  /** Where the reader was when they asked for an older page, kept until that page lands above it. */
  const olderPageAnchorRef = useRef<{ scrollHeight: number; scrollTop: number } | undefined>(undefined);

  const visibleForums: Forum[] = useMemo(() => {
    if (readOnly) {
      // A room the reader left stays, however few are still in it: it is theirs to read up to when they
      // left (#5833). Taking a member off can leave a room with only its leader.
      return forums?.filter((forum) => (forum?.subscribers?.length ?? 0) > 1 || forum?.visibleUntil !== undefined) ?? [];
    }
    return forums?.filter((forum) => forum.id) ?? [];
  }, [forums, readOnly]);
  // Built from the ids' text, not from the forums, so a parent that re-renders with a fresh array of
  // the same forums does not hand the shared files query a new dependency and reload it. It reaches
  // hook dependencies, so it must stay the same array until the set of open forums changes.
  const visibleForumIdList = visibleForums.map((forum) => forum.id?.value ?? "").filter(Boolean).join(",");
  const visibleForumIds: Guid[] = useMemo(
    () => (visibleForumIdList ? visibleForumIdList.split(",").map((forumId) => new Guid(forumId)) : []),
    [visibleForumIdList],
  );
  // #region State
  const [activeForumId, setActiveForumId] = useState<string | undefined>();
  const [activeForumSubscriberData, setActiveForumSubscriberData] = useState<ActiveForumSubscriberData>({
    participants: [],
    avatars: {},
  });
  const [searchString, setSearchString] = useState<string | undefined>();
  const [minCreatedDate, setMinCreatedDate] = useState<string | undefined>();
  const [maxCreatedDate, setMaxCreatedDate] = useState<string | undefined>();
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
  const [isLoadingForum, setIsLoadingForum] = useState(false);
  const [backToTopVisible, setBackToTopVisible] = useState(false);
  const [showSystemMessages, setShowSystemMessages] = useState(false);
  const [showSharedFiles, setShowSharedFiles] = useState(false);
  const [filterDates, setFilterDates] = useState(false);
  // How the timeline came up: grabbing the scrollbar brings it up unasked, so it fades when left alone.
  const [timelineOpenedBy, setTimelineOpenedBy] = useState<"scrollbar" | "toggle">();
  const timelineActive = timelineOpenedBy !== undefined;
  const [formattingEnabled, setFormattingEnabled] = useState(false);
  const [isJumpingToUnread, setIsJumpingToUnread] = useState(false);
  // A link's request is honored once its forum is among the visible ones, which can be a render or two
  // after the window mounts: the forums arrive from the store. Adjusted while rendering, so the window
  // never paints the default tab first and then jumps.
  //
  // Honoring it is choosing that forum's tab, so it resets the view as `selectForum` does. A window that
  // stays mounted across a link to the same agreement may be on Shared files, and choosing the forum
  // alone would leave the files on screen over the conversation the link opened. The resets are written
  // out here rather than called, because `showView` is declared below and runs no earlier than an event.
  const requestedForumId = findRequestedForum(visibleForums, forumRequest?.forum, describeConversation)?.id?.value;
  const [honoredForumRequestKey, setHonoredForumRequestKey] = useState<string>();
  // A link also asks for the forum's oldest unread message (#6585). It is answered once the forum's
  // first page has loaded since the link, because messages an earlier visit left in the store are not
  // the conversation as it stands now.
  const [unreadArrival, setUnreadArrival] = useState<UnreadArrival>();
  // The message a link's arrival found, until its row is on screen and the pane has scrolled to it.
  const [arrivalRevealId, setArrivalRevealId] = useState<string>();
  if (forumRequest && requestedForumId && forumRequest.key !== honoredForumRequestKey) {
    setHonoredForumRequestKey(forumRequest.key);
    setUnreadArrival({ key: forumRequest.key, forumId: requestedForumId, loadStarted: false });
    setArrivalRevealId(undefined);
    setActiveForumId(requestedForumId);
    setBackToTopVisible(false);
    if (showSharedFiles) {
      setInitialLoadCompleted(false);
      setSearchString(undefined);
      setMinCreatedDate(undefined);
      setMaxCreatedDate(undefined);
      setFilterDates(false);
      setTimelineOpenedBy(undefined);
      setShowSharedFiles(false);
    }
  }
  const activeForum = useMemo(() => {
    return visibleForums.find((forum) => forum.id?.value === activeForumId) ?? visibleForums[0];
  }, [activeForumId, visibleForums]);
  const activeForumParticipants = useMemo(
    () =>
      activeForumSubscriberData.forumId === activeForum?.id?.value
        ? activeForumSubscriberData.participants
        : [],
    [activeForum?.id?.value, activeForumSubscriberData],
  );
  const activeForumAvatars =
    activeForumSubscriberData.forumId === activeForum?.id?.value ? activeForumSubscriberData.avatars : {};
  const listedForumSubscribers = useMemo(
    () =>
      activeForumParticipants
        .map((participant) => participant.individual)
        .filter((individual) =>
          activeForum?.subscribers?.some((subscriber) => subscriber.userId.isEqualTo(individual.userId)),
        ),
    [activeForum?.subscribers, activeForumParticipants],
  );

  // #region Selectors
  const sharedFiles = useSelector(getSharedFiles(visibleForumIds));
  const isLoadingSharedFiles = useSelector(getIsLoadingSharedFiles(visibleForumIds));
  const messages = useSelector(getMessagesByForumId(activeForum?.id));
  const unreadCounts = useSelector(getForumUnreadCounts);
  const isLoadingMessages = useSelector(getIsLoadingMessagesByForumId(activeForum?.id));
  const latestMessageId = messages.data?.at(-1)?.id?.value;
  if (unreadArrival && !unreadArrival.loadStarted && isLoadingMessages && activeForum?.id?.value === unreadArrival.forumId) {
    setUnreadArrival({ ...unreadArrival, loadStarted: true });
  }

  /**
   * The two ways this pane waits, told apart by whether it has anything to show yet.
   *
   * They used to be three independent booleans read straight into three sibling conditions, and on
   * a first open all three were true: the pane opened with a sticky spinner at the top, a second
   * differently-sized one captioned "Loading..." in the middle, and nothing else. Deriving the two
   * states from the same pair of facts is what stops them co-occurring -- the skeleton renders only
   * while the list is empty, and the top spinner only while it is not, so neither can appear over
   * the other however the three fetches happen to interleave.
   */
  const activeListIsEmpty = showSharedFiles ? sharedFiles.isEmpty : messages.isEmpty;
  const isLoadingActiveList = showSharedFiles ? isLoadingSharedFiles : isLoadingMessages;
  // The empty notice fills the pane, so an empty list of either kind would otherwise scroll by the
  // pane's top padding.
  const hideMessagePaneScrollbar = activeListIsEmpty;
  // A locked forum hides its messages behind the lock notice, however many it holds.
  const listShowsNotice = activeListIsEmpty || (Boolean(locked) && !showSharedFiles);
  const timelineRange = useForumTimelineRange({
    active: timelineActive,
    filtering: filterDates,
    listFirstDate: showSharedFiles ? sharedFiles.minCreatedDate : messages.minCreatedDate,
    listLastDate: showSharedFiles ? sharedFiles.maxCreatedDate : messages.maxCreatedDate,
    listLoading: isLoadingActiveList,
    // Both lists grow by realtime while the scrubber is open. The shared files are kept newest first,
    // so their newest is the first entry; messages land where they arrive, so they are scanned.
    newestMessageDate: showSharedFiles ? sharedFiles.data[0]?.createdDate : newestCreatedDate(messages.data),
  });
  const closeTimeline = useCallback(() => setTimelineOpenedBy(undefined), []);
  const timelineFade = useTimelineIdleFade({ enabled: timelineOpenedBy === "scrollbar", onFaded: closeTimeline });
  const { noteActivity: noteTimelineActivity } = timelineFade;
  /**
   * Nothing on screen, and something on its way to put something there.
   *
   * `isLoadingForum` belongs here rather than beside the list check because the content below is
   * withheld while it is true whatever the list holds -- so a forum still resolving its subscribers
   * leaves the pane blank even when messages have already arrived by realtime, which is precisely
   * the case that put a lone spinner over an empty pane.
   */
  const isFillingEmptyPane = isLoadingForum || (activeListIsEmpty && isLoadingActiveList);
  /**
   * A further page, over content the reader is already looking at.
   *
   * Defined against the state above rather than alongside it, so the two cannot both be true by
   * construction however the three fetches interleave.
   */
  const isLoadingOlderPage = !isFillingEmptyPane && isLoadingActiveList;

  // #region Hooks
  const { openDialog, closeDialog } = useDialog();
  const { openImagePreviewDialog } = useImagePreviewDialog();
  const session = useSession();
  const currentUserName = session.user?.fullName;
  const forumLabels = useMemo(
    () =>
      new Map(
        visibleForums.map((forum) => [forum.id?.value ?? "", getForumChatTitle(forum, currentUserName) || "Messages"]),
      ),
    [currentUserName, visibleForums],
  );
  const isActiveForumCreator = activeForum?.creator?.userId.isEqualTo(session.user?.id) ?? false;
  const readState = useForumReadState({
    forum: activeForum,
    messages: messages.data ?? [],
    userId: session.user?.id,
    isOpen: isOpen && !showSharedFiles && !locked,
    // Undefined until the first fetch starts. That is not "loaded": read as loaded, the sweep found an
    // empty list on the first render, counted the visit as read, and opening the pane marked nothing.
    isLoading: isLoadingMessages !== false,
    serverUnreadCount: unreadCounts[activeForum?.id?.value ?? ""],
  });
  const dispatch = useAttorneyHubDispatch();
  useInfiniteScroll(
    // Older messages load at the top of the conversation
    fetchMoreMessages,
    !showSharedFiles && messages.hasNextPage,
    isLoadingMessages,
    messagePaneRef,
    true
  );
  useInfiniteScroll(
    // Older shared files load at the bottom: the list is newest first
    fetchMoreSharedFiles,
    showSharedFiles && sharedFiles.hasNextPage,
    isLoadingSharedFiles,
    messagePaneRef,
    false
  );

  const fetchUserAvatars = useCallback(
    async (
      subscribers: Individual[]
    ): Promise<UserAvatarMap> => {
      let userAvatars: UserAvatarMap = {};
      for (let subscriber of subscribers) {
        if (!subscriber.profile?.avatarId) continue;
        if (userAvatars[subscriber.id.value] && userAvatars[subscriber.userId.value]) continue;

        try {
          const avatarData = await dispatch(
            individualsApi.endpoints.getIndividualAvatar.initiate({ individualId: subscriber.id.value, size: "small" }),
          ).unwrap();
          if (avatarData) {
            userAvatars[subscriber.id.value] = avatarData;
            userAvatars[subscriber.userId.value] = avatarData;
          }
        } catch {
          // Avatar failures are non-blocking. Keep the failure private and continue loading the chat.
        }
      }
      return userAvatars;
    },
    [dispatch]
  );

  const loadSubscribers = useCallback(
    (forum: Forum, abortController: AbortController) =>
      loadForumSubscribers(forum, abortController, dispatch, fetchUserAvatars, setActiveForumSubscriberData),
    [dispatch, fetchUserAvatars]
  );


  const handleWindowScrolled = useCallback(() => {
    const pane = messagePaneRef.current;
    if (!pane) return;

    const { scrollTop, scrollHeight, clientHeight } = pane;
    const maxScroll = scrollHeight - clientHeight;

    // Hide the back to top button if there's nothing to scroll
    if (maxScroll <= 0) {
      setBackToTopVisible(false);
      return;
    }

    // Otherwise, offer the chip while the reader has left the bottom
    const scrollPercent = Math.round((scrollTop / maxScroll) * 100);
    setBackToTopVisible(scrollPercent < 99);

    // Once the reader has been brought down to the newest messages, a scroll to the top is theirs and
    // loads the older page. Within a pane's height of the foot is down: waiting for exactly the foot
    // missed a reader who scrolled up while the opening scroll was still under way, and left them
    // unable to load older messages until they went back to the bottom (#6760).
    if (!initialLoadCompleted && maxScroll - scrollTop <= clientHeight) {
      setInitialLoadCompleted(true);
    }
  }, [initialLoadCompleted]);

  const scrollToBottom = useCallback(
    (delayMs: number = 0) => {
      if (!bottomRef.current) return;
      setTimeout(() => {
        bottomRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }, delayMs);
    },
    [bottomRef]
  );

  // Opened at the newest message, and held there while the list settles under it (#6138).
  const followsNewestMessage = usePaneFollowsNewestMessage({
    paneRef: messagePaneRef,
    listRef: messageListRef,
    enabled: !showSharedFiles,
    listKey: [activeForum?.id?.value, showSharedFiles, showSystemMessages, searchString, minCreatedDate, maxCreatedDate].join("|"),
  });

  const loadActiveForumSubscribersAndAvatars = useCallback(
    (forum: Forum, abortController: AbortController) =>
      loadForumSubscribersAndAvatars(forum, abortController, loadSubscribers, setIsLoadingForum),
    [loadSubscribers]
  );

  const loadSharedFiles = useCallback(() => {
    if (visibleForumIds.length === 0) return;

    const query = new AttachmentQuery(
      new AttachmentParameters(new AttachmentFilterParameters(searchString, minCreatedDate, maxCreatedDate)),
      PaginationParameters.create({
        pageIndex: 0,
        pageSize: Config.chatPageSize,
      })
    );
    dispatch(
      populateSharedFiles({
        forumIds: visibleForumIds,
        query,
        append: !searchString && !filterDates,
      })
    );
  }, [dispatch, filterDates, maxCreatedDate, minCreatedDate, searchString, visibleForumIds]);

  const populateMessages = useCallback(() => {
    if (!activeForum?.id) return;

    const query = new MessageQuery(
      new MessageParameters(
        new MessageFilterParameters({
          searchString,
          systemOnly: showSystemMessages,
          minCreatedDate,
          maxCreatedDate,
          // Page 0 is the newest page, so a chat longer than a page opens on its newest message (#6760).
          newestFirst: true,
        })
      ),
      PaginationParameters.create({
        pageIndex: 0,
        pageSize: Config.chatPageSize,
      })
    );
    return dispatch(
      messagingApi.endpoints.getMessagesByForum.initiate({
        forumId: activeForum.id.value,
        query,
        append: !searchString && !showSystemMessages && !filterDates,
      }, { forceRefetch: true })
    );
  }, [activeForum, dispatch, filterDates, maxCreatedDate, minCreatedDate, searchString, showSystemMessages]);

  // #region Lifecycle
  // 1. DOM Event Listeners (Runs once on mount)
  useEffect(() => {
    const pane = messagePaneRef.current;
    if (!pane) return;

    // Listening from the start: a reader who reached the newest message and scrolled straight back up inside
    // the half second was never seen at the foot, and could load no older page (#6760).
    pane.addEventListener("scroll", handleWindowScrolled, { passive: true });
    const timeoutId = globalThis.setTimeout(() => {
      pane.addEventListener("scroll", noteTimelineActivity, { passive: true });
      // The pane may already sit at the newest message, put there before this listener was attached, and
      // then no scroll event says so.
      handleWindowScrolled();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      pane.removeEventListener("scroll", handleWindowScrolled);
      pane.removeEventListener("scroll", noteTimelineActivity);
    };
  }, [handleWindowScrolled, noteTimelineActivity]);

  useScrollbarPress(messagePaneRef, () => {
    // A timeline already open with its toggle stays a toggled one.
    setTimelineOpenedBy((openedBy) => openedBy ?? "scrollbar");
    noteTimelineActivity();
  });

  // 2. Load subscribers for the active forum
  useEffect(() => {
    if (!activeForum) return

    const abortController = new AbortController();
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      void loadActiveForumSubscribersAndAvatars(activeForum, abortController);
    });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [activeForum, loadActiveForumSubscribersAndAvatars]);

  // 4. Scroll to the newest entry: the bottom of the conversation, the top of the shared files
  useEffect(() => {
    if (showSharedFiles) {
      messagePaneRef.current?.scrollTo?.({ top: 0 });
      return;
    }
    // Only a first page opens at the newest message. An older page lands above the reader, who stays put.
    if (isLoadingMessages || messages.pageIndex > 0) return;
    scrollToBottom();
  }, [isLoadingMessages, messages.pageIndex, scrollToBottom, showSharedFiles, showSystemMessages]);

  // The older page has landed above what the reader was looking at and pushed it down by its own height,
  // so move the scroll down by the same amount (#6760).
  useLayoutEffect(() => {
    const anchor = olderPageAnchorRef.current;
    const pane = messagePaneRef.current;
    if (!anchor || !pane || isLoadingMessages) return;
    olderPageAnchorRef.current = undefined;
    pane.scrollTop = anchor.scrollTop + pane.scrollHeight - anchor.scrollHeight;
  }, [isLoadingMessages, messages.data]);

  useEffect(() => {
    if (showSharedFiles || !latestMessageId) return;
    scrollToBottom();
  }, [latestMessageId, scrollToBottom, showSharedFiles]);

  useEffect(() => {
    if (showSharedFiles || !activeForum) return;
    populateMessages();
  }, [activeForum, populateMessages, showSharedFiles]);

  // The shared files reload whenever the open forums change, so opening or closing a tab while the
  // list is showing brings it up to date.
  // Not while a Documents panel stands in for the list: it loads its own.
  useEffect(() => {
    if (!showSharedFiles || documents) return;
    loadSharedFiles();
  }, [documents, loadSharedFiles, showSharedFiles]);
  // #endregion

  // #region Functions
  function fetchMoreMessages() {
    if (!activeForum?.id || !messages.hasNextPage || isLoadingForum || !initialLoadCompleted) return;

    const query = new MessageQuery(
      new MessageParameters(
        new MessageFilterParameters({
          searchString,
          systemOnly: showSystemMessages,
          minCreatedDate,
          maxCreatedDate,
          newestFirst: true,
        })
      ),
      PaginationParameters.create({
        pageIndex: messages.pageIndex + 1,
        pageSize: messages.pageSize,
      })
    );
    // The reader is reading back: the pane no longer holds them at the newest message, and the place they
    // are at is kept until the older page lands above it.
    followsNewestMessage.release();
    const pane = messagePaneRef.current;
    if (pane) olderPageAnchorRef.current = { scrollHeight: pane.scrollHeight, scrollTop: pane.scrollTop };
    dispatch(
      messagingApi.endpoints.getMessagesByForum.initiate({
        forumId: activeForum.id.value,
        query,
        append: true,
      }, { subscribe: false })
    );
  }

  // A scroll to the top that came while the pane could not load (a page or the forum still loading) is not
  // repeated: at the top a further scroll up fires no event. So once the pane can load again, a reader left at
  // the top gets the older page they scrolled up for (#6760).
  const loadOlderPageForReaderAtTop = useEffectEvent(() => {
    const pane = messagePaneRef.current;
    if (!pane || pane.scrollTop > olderPageScrollThreshold) return;
    fetchMoreMessages();
  });
  useEffect(() => {
    if (showSharedFiles || isLoadingMessages || isLoadingForum) return;
    loadOlderPageForReaderAtTop();
  }, [isLoadingForum, isLoadingMessages, showSharedFiles]);

  function fetchMoreSharedFiles() {
    if (visibleForumIds.length === 0 || !sharedFiles.hasNextPage) return;

    const query = new AttachmentQuery(
      new AttachmentParameters(new AttachmentFilterParameters(searchString, minCreatedDate, maxCreatedDate)),
      PaginationParameters.create({
        pageIndex: sharedFiles.pageIndex + 1,
        pageSize: sharedFiles.pageSize,
      })
    );
    dispatch(
      populateSharedFiles({
        forumIds: visibleForumIds,
        query,
        append: true,
      })
    );
  }

  function handleTimelineDateSelected(startDate?: string, endDate?: string) {
    if (!startDate || !endDate) {
      closeTimeline();
      return;
    }
    // A pick is the plainest sign the user wants the timeline, however it came up.
    timelineFade.engage();
    setFilterDates(true);
    setMinCreatedDate(startDate);
    setMaxCreatedDate(endDate);
  }

  function handleTimelineReset() {
    timelineFade.engage();
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setFilterDates(false);
  }

  // Closes the scrubber as well: its range and levels belong to the list it was opened over.
  function clearDateFilter() {
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setFilterDates(false);
    closeTimeline();
  }

  // A date filter belongs to the forum it was picked in. Another forum's messages start unfiltered.
  const [dateFilterForumId, setDateFilterForumId] = useState(activeForum?.id?.value);
  if (dateFilterForumId !== activeForum?.id?.value) {
    setDateFilterForumId(activeForum?.id?.value);
    clearDateFilter();
  }

  function handleAddSubscriberPressed(): void {
    if (!activeForum) return;
    const component = <AddSubscriber forum={activeForum} onClosed={closeDialog} />;
    openDialog({ component: component, title: "Add People to Chat" });
  }

  function handleLeaveConversationPressed(): void {
    onLeaveConversation?.();
    closeDialog();
  }

  /**
   * Moves between a forum's messages and the shared files. Each view has its own search and date
   * range, so the ones set in the view being left are cleared rather than carried across.
   */
  function showView(sharedFilesView: boolean) {
    if (sharedFilesView === showSharedFiles) return;
    setBackToTopVisible(false);
    setInitialLoadCompleted(false);
    setSearchString(undefined);
    clearDateFilter();
    setShowSharedFiles(sharedFilesView);
  }

  function selectForum(forumId: string | undefined) {
    if (!visibleForums.some((forum) => forum.id?.value === forumId)) return;
    // The chip reflects the pane as the last scroll left it, and a shorter list fires no scroll to
    // correct it: switching lists hides it until the new one is scrolled.
    if (forumId !== activeForum?.id?.value) setBackToTopVisible(false);
    // Choosing a tab by hand ends a link's arrival: the reader has moved on from it.
    setUnreadArrival(undefined);
    setArrivalRevealId(undefined);
    setActiveForumId(forumId);
    showView(false);
  }

  /**
   * Scrolls the pane, and only the pane, to the message. After the pages it loaded have rendered, and
   * after the scroll to the foot of the list that ends every load. `onDone` hears whether the message's
   * row was there to scroll to.
   */
  function revealMessage(messageId: string, onDone?: (revealed: boolean) => void) {
    followsNewestMessage.release();
    globalThis.setTimeout(() => {
      const pane = messagePaneRef.current;
      const row = pane?.querySelector(`[data-message-id="${messageId}"]`);
      if (!pane || !row) {
        onDone?.(false);
        return;
      }
      // Centred in the pane, so nothing pinned over the top of the list can cover it, whatever its
      // height. An offset for the search row broke as soon as the row changed size.
      const rowBox = row.getBoundingClientRect();
      const rowTop = rowBox.top - pane.getBoundingClientRect().top;
      pane.scrollTop += rowTop - (pane.clientHeight - rowBox.height) / 2;
      onDone?.(true);
    }, 100);
  }

  /** Loads back to the oldest unread message, and hands its id to `onFound`. */
  function jumpToOldestUnread(onFound: (messageId: string) => void) {
    const forumId = activeForum?.id?.value;
    const readerId = session.user?.id;
    if (!forumId || !readerId) return;
    setIsJumpingToUnread(true);
    void loadOldestUnreadMessageId(dispatch, forumId, readerId).then((messageId) => {
      setIsJumpingToUnread(false);
      if (messageId) onFound(messageId);
    });
  }

  /**
   * Brings the oldest unread message on screen, however far back it is: older pages load until it is in
   * the list. A search, a date range or the system-messages filter would hide it, so the control is
   * offered only without them.
   */
  function handleJumpToOldestUnread() {
    jumpToOldestUnread((messageId) => revealMessage(messageId));
  }

  // A link that names a forum lands on its oldest unread message, as Jump to oldest unread does, once
  // the forum's first page is in (#6585). With nothing unread the pane stays at the newest message.
  // It runs before the read sweep's write comes back, so the messages the sweep marks still read as
  // unread here. A microtask, so the jump's own state is not set in the effect's body.
  const answeredArrivalKey = useRef<string>(undefined);
  const arrivalIsReady = Boolean(
    unreadArrival?.loadStarted &&
      isLoadingMessages === false &&
      activeForum?.id?.value === unreadArrival.forumId &&
      isOpen &&
      !locked &&
      !showSharedFiles &&
      !searchString &&
      !showSystemMessages &&
      !filterDates,
  );
  const arrivalKey = unreadArrival?.key;
  const hasUnread = readState.unreadCount > 0;
  useEffect(() => {
    if (!arrivalIsReady || !arrivalKey || answeredArrivalKey.current === arrivalKey) return;
    answeredArrivalKey.current = arrivalKey;
    if (!hasUnread) return;
    queueMicrotask(() => jumpToOldestUnread(setArrivalRevealId));
  });

  // The message the arrival found is scrolled to once its row is on screen. The skeleton stands in for
  // every row while the forum's members load, and those can arrive after the messages, so an attempt
  // that finds no row leaves the message pending and the next render tries again. Only a scroll that
  // reached the row ends the arrival.
  const revealInFlight = useRef(false);
  useEffect(() => {
    if (!arrivalRevealId || isFillingEmptyPane || revealInFlight.current) return;
    revealInFlight.current = true;
    revealMessage(arrivalRevealId, (revealed) => {
      revealInFlight.current = false;
      if (revealed) setArrivalRevealId((pending) => (pending === arrivalRevealId ? undefined : pending));
    });
  });

  function handleToggleSystemMessages() {
    setBackToTopVisible(false);
    setInitialLoadCompleted(false);
    setSearchString(undefined);
    clearDateFilter();
    setShowSystemMessages(!showSystemMessages);
  }

  function handleToggleFilterDates(): void {
    if (filterDates) {
      clearDateFilter();
    } else {
      setTimelineOpenedBy("toggle");
    }
  }

  const handleSearch = useCallback((searchString: string): void => {
    setSearchString(searchString);
    setBackToTopVisible(false);
    if (searchString === "") {
      scrollToBottom(1000);
    }
  }, [scrollToBottom]);

  function getEmptyMessageString(): string {
    if (locked) {
      return lockedMessage ?? "Chat was locked by the moderator";
    }
    if (searchString) {
      return "No Matching Results";
    }
    return "No Messages";
  }

  // Declared before `openImagePreview`, which closes over it: the React Compiler cannot rewrite a
  // reference to a function that is only hoisted into scope, and skips the component when it finds one.
  async function downloadAttachment(message: Message, attachment: MessageFileInfo): Promise<void> {
    // Handled up front rather than thrown into the catch below: the React Compiler cannot lower a
    // `throw` inside a try block, and the outcome for the user is identical.
    const forumId = message.forum.id;
    if (!forumId) {
      showErrorSnackbar(new Error("The message names no forum to download its file from."), ATTACHMENT_DOWNLOAD_FAILURE);
      return;
    }

    try {
      const request = messagingApi.endpoints.downloadMessageAttachment.initiate({
          forumId: forumId.value,
          fileId: attachment.id.value,
        },
        { subscribe: false }
      );
      const runningRequest = dispatch(request);
      const response = await runningRequest.unwrap();
      await downloadFile(response, attachment.name);
    } catch (error) {
      showErrorSnackbar(error, ATTACHMENT_DOWNLOAD_FAILURE);
    }
  }

  // The messages on screen are the active forum's, so that is the conversation a file was sent in.
  const copyAttachment =
    onCopyAttachment && activeForum
      ? (_message: Message, attachment: MessageFileInfo) => onCopyAttachment(attachment, activeForum)
      : undefined;

  function openImagePreview(message: Message, image: MessageFileInfo) {
    if (!image.dataUri) {
      enqueueSnackbar("Image not found", { variant: "error" });
      return;
    }
    onClose?.();
    openImagePreviewDialog(
      {
        name: image.name,
        src: image.dataUri,
      },
      () => downloadAttachment(message, image)
    );
  }
  // #endregion

  // #region Render
  const showsDocuments = showSharedFiles && Boolean(documents);
  // The shared files stay newest first regardless of conversation order, so their older page always
  // arrives at the foot of the pane; the messages' older page arrives at the head.
  const olderPageLoadingIndicator = isLoadingOlderPage && (
    <OlderPageLoadingIndicatorContainer atFoot={showSharedFiles}>
      <MessageLoadingIndicator disableShrink thickness={4} />
    </OlderPageLoadingIndicatorContainer>
  );
  const listHasSomethingToJumpTo = !showSharedFiles && !messages.isEmpty;
  const jumpToBottomChip = listHasSomethingToJumpTo && (
    <CorrespondenceJumpToBottom visible={backToTopVisible} onClick={() => scrollToBottom()} />
  );
  // A forum can refuse the reader on its own: they left the work, or the work has ended (#5833). The
  // server refuses the write either way; this keeps the page from offering it.
  const readOnlyNotice = getForumReadOnlyNotice(activeForum);
  // Who reads the open conversation. The shared files span every open forum, so they have no one audience.
  const audienceNotice = activeForum && !showSharedFiles ? describeConversation?.(activeForum)?.audienceNotice : undefined;
  const activeForumReadOnly = readOnly || readOnlyNotice !== undefined;
  // The shared files span every open forum, so there is no one forum a message typed there would go to.
  const readOnlyNoticeSlot = readOnlyNotice && !showSharedFiles && (
    <CorrespondenceComposerSlot>
      <ReadOnlyNotice role="status" data-testid="chat-read-only-notice">
        {readOnlyNotice}
      </ReadOnlyNotice>
    </CorrespondenceComposerSlot>
  );
  const messageEditor = !activeForumReadOnly && !showSharedFiles && (
    <CorrespondenceComposerSlot>
      <MessageEditor
        forum={activeForum}
        allowAttachments={activeForum?.acceptsAttachments() ?? true}
        onFormattingToggled={(enabled) => {
          setFormattingEnabled(enabled);
          scrollToBottom(1000);
        }}
        onAttachmentAdded={() => scrollToBottom(1000)}
        onMessageSent={() => scrollToBottom(1000)}
        attachmentOption={attachmentOption}
        locked={locked}
      />
    </CorrespondenceComposerSlot>
  );

  return (
    <CorrespondenceFrame
      className={className}
      data-chat-layout={fillAvailableSpace ? "fill" : undefined}
      fillAvailableSpace={fillAvailableSpace}
    >
      <CorrespondenceTabs
        value={showSharedFiles ? SHARED_FILES_TAB : (activeForum?.id?.value ?? false)}
        onChange={(newValue) => {
          if (newValue === SHARED_FILES_TAB) {
            showView(true);
          } else {
            selectForum(newValue);
          }
        }}
        conversations={visibleForums.map((forum: Forum) => ({
          value: forum.id?.value ?? "",
          title: describeConversation?.(forum)?.title ?? (getForumChatTitle(forum, currentUserName) || "Messages"),
          shortTitle: describeConversation?.(forum)?.shortTitle,
          // Every tab counts what the server last said: the whole conversation, loaded or not, kept
          // current by realtime and by every read (#5828).
          unreadCount:
            forum === activeForum
              ? readState.unreadCount
              : (unreadCounts[forum.id?.value ?? ""] ?? forum.numUnreadMessages),
          // A locked window keeps the reader on one forum, but the Shared files tab still has to lead
          // back to it. Read-only stops posting, not reading: every forum stays a tab away (#7081).
          disabled: Boolean(locked) && !(showSharedFiles && forum === activeForum),
        }))}
        onClose={onClose}
        showSharedFiles={visibleForums.length > 0}
        sharedFilesDisabled={locked}
        filesTab={documents ? DOCUMENTS_TAB : undefined}
        // The subscribers are loaded either way: the messages take their senders and avatars from them.
        trailing={
          !hideMembers && (
            <SubscribersComponent
              subscribers={listedForumSubscribers}
              forum={activeForum}
              disableAddButton={!isActiveForumCreator}
              avatars={activeForumAvatars}
              disableLeaveButton={locked || readOnly}
              onAddSubscriber={handleAddSubscriberPressed}
              onLeaveConversation={handleLeaveConversationPressed}
            />
          )
        }
      />
      {showsDocuments && <DocumentsSlot>{documents}</DocumentsSlot>}
      {/* Hidden rather than unmounted behind the Documents panel: the pane's scroll listeners are
          attached once, to this pane, and a remount would leave the conversation without them. */}
      <CorrespondenceBody formattingEnabled={formattingEnabled} style={showsDocuments ? { display: "none" } : undefined}>
        <CorrespondenceHeader audienceNotice={audienceNotice}>
          <CorrespondenceSearchBar
            // Remounted per view, so the text typed into one view's search does not stay in the box
            // after the search itself was cleared on the way to the other.
            key={showSharedFiles ? SHARED_FILES_TAB : "messages"}
            showSystemMessages={showSystemMessages}
            filterDates={filterDates}
            onToggleDates={handleToggleFilterDates}
            onToggleSystemMessages={handleToggleSystemMessages}
            onSearch={handleSearch}
            onMarkAllRead={!locked && !showSharedFiles ? readState.markAllRead : undefined}
            markAllReadDisabled={readState.unreadCount === 0}
            onJumpToOldestUnread={
              !locked && !showSharedFiles && !searchString && !showSystemMessages && !filterDates
                ? handleJumpToOldestUnread
                : undefined
            }
            jumpToOldestUnreadDisabled={readState.unreadCount === 0 || isJumpingToUnread}
          />
        </CorrespondenceHeader>
        <MessageListFrame>
          <MessagePane ref={messagePaneRef} hideScrollbar={hideMessagePaneScrollbar} data-testid="message-pane">
            <MessageList ref={messageListRef} showsNotice={listShowsNotice}>
              {!showSharedFiles && olderPageLoadingIndicator}
              {isFillingEmptyPane && (
                <ForumChatLoadingSkeleton variant={showSharedFiles ? "attachments" : "messages"} />
              )}
              {!isFillingEmptyPane && (
                <>
                  {showSharedFiles && (
                    <SharedFilesList
                      files={sharedFiles.data}
                      isLoading={isLoadingSharedFiles}
                      isSearching={Boolean(searchString)}
                      forumLabels={forumLabels}
                    />
                  )}
                  {!showSharedFiles && (
                    <>
                      {(locked || (messages.isEmpty && !isLoadingMessages)) && <CorrespondenceEmptyText>{getEmptyMessageString()}</CorrespondenceEmptyText>}
                      {/* A divider above the first message of each day; the bubbles under it give only the
                          time (#7082). */}
                      {!locked &&
                        withConversationDays(messages.data ?? [], messageMomentOf).map(({ item: message, opensDay }) => (
                          <Fragment key={message.id?.value}>
                            {opensDay && <ConversationDayDivider date={opensDay} />}
                            {session.user && message.userIsSender(session.user) ? (
                              <SentMessage
                                data-message-id={message.id?.value}
                                data-date={message.created?.format("MM/DD/YY hh:mm A")}
                                message={message}
                                onImageLoad={() => scrollToBottom(1000)}
                                onImageClick={openImagePreview}
                                onDownload={downloadAttachment}
                                onCopyToDocuments={copyAttachment}
                                onEdit={activeForumReadOnly ? undefined : (edited, text) => saveMessageEdit(dispatch, edited, text)}
                                onDelete={activeForumReadOnly ? undefined : (deleted) => deleteOwnMessage(dispatch, deleted)}
                              />
                            ) : (
                              <ReceivedMessage
                                data-message-id={message.id?.value}
                                data-date={message.created?.format("MM/DD/YY hh:mm A")}
                                sender={activeForumParticipants.find((participant) =>
                                  participant.individual.userId?.isEqualTo(message.senderId),
                                )}
                                avatars={activeForumAvatars}
                                message={message}
                                onImageLoad={() => scrollToBottom(1000)}
                                onImageClick={openImagePreview}
                                onDownload={downloadAttachment}
                                onCopyToDocuments={copyAttachment}
                                unread={readState.isUnread(message)}
                                readStateDisabled={readState.isUpdating(message)}
                                onReadStateToggled={readState.toggle}
                              />
                            )}
                          </Fragment>
                        ))}
                    </>
                  )}
                </>
              )}
              {showSharedFiles && olderPageLoadingIndicator}
            </MessageList>
            <div ref={bottomRef} />
          </MessagePane>
          <TimelineOverlay
            key={timelineRange.generation}
            show={timelineActive}
            fading={timelineFade.fading}
            onHover={timelineFade.hover}
            onEngaged={timelineFade.engage}
            firstDate={timelineRange.firstDate}
            lastDate={timelineRange.lastDate}
            onDateSelected={handleTimelineDateSelected}
            onReset={handleTimelineReset}
          />
        </MessageListFrame>
        {jumpToBottomChip}
      </CorrespondenceBody>
      {messageEditor}
      {readOnlyNoticeSlot}
    </CorrespondenceFrame>
  );
  // #endregion
}
